import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import { useBranding } from '../App';
import { useIsMobile } from '../utils';
import { useTheme } from '../hooks/useTheme';
import { Sun, Moon } from 'lucide-react';
import Logo from './Logo';
import { api } from '../api';

// #184 c / #189 — texto del aviso cuando la base no tiene todas las migraciones del código.
export function pendingMigrationsMessage(info) {
  if (!info || !info.pending) return null;
  const join = (v) => (Array.isArray(v) && v.length ? v.join(', ') : '—');
  return `La base no tiene aplicadas todas las migraciones del código (aplicada ${join(info.alembic_applied)}, esperada ${join(info.alembic_heads)}).`;
}

// Miga de pan en castellano: el primer tramo con el nombre del menú; un segundo tramo (el id de un
// cliente, por ejemplo) es siempre «Detalle».
const CRUMBS = {
  dashboard: 'Dashboard', clients: 'Clientes', jobs: 'Jobs', logs: 'Logs', users: 'Usuarios',
  resellers: 'Resellers', restores: 'Restaurar', updates: 'Actualizaciones', settings: 'Configuración', audit: 'Auditoría',
  profile: 'Mi perfil',
};
export function breadcrumbLabel(pathname) {
  const [first, second] = pathname.split('/').filter(Boolean);
  if (!first) return CRUMBS.dashboard;
  const label = CRUMBS[first] || first.charAt(0).toUpperCase() + first.slice(1);
  return second ? `${label} / Detalle` : label;
}

export default function Layout({ children, onLogout }) {
  const navigate      = useNavigate();
  const location      = useLocation();
  const auth          = useAuth();
  const { branding }  = useBranding();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isMobile        = useIsMobile();
  const { theme, toggleTheme } = useTheme();
  const isReseller    = auth?.isReseller;
  const perms         = auth?.perms || {};
  const resellerPerms = auth?.resellerPerms || {};
  const me            = auth?.me;
  const [systemInfo, setSystemInfo] = useState(null);
  const isAdmin       = me?.role === 'admin';

  // Sólo admin: el endpoint es require_admin, y para el resto el pedido ni se dispara (lección del #212).
  useEffect(() => {
    if (!isAdmin) return undefined;
    let alive = true;
    api.getSystemInfo().then(i => { if (alive) setSystemInfo(i); }).catch(() => {});
    return () => { alive = false; };
  }, [isAdmin]);
  const pendingMsg = isAdmin ? pendingMigrationsMessage(systemInfo) : null;

  const allNav = [
    { path: '/dashboard', label: 'Dashboard',    icon: '◈', roles: ['admin','operator','client','reseller'] },
    { path: `/clients/${me?.client_id}`, label: 'Mis Servidores', icon: '⬡', roles: ['admin','operator','client'], show: !!me?.client_id && !isReseller, perms: ['can_view_clients'] },
    { path: '/clients',   label: 'Clientes',     icon: '◉', roles: ['admin','operator'],          perms: ['can_view_clients'] },
    { path: '/clients',   label: 'Mis Clientes', icon: '◉', roles: ['reseller'],                  resellerPerm: 'can_view_clients' },
    { path: '/jobs',      label: 'Jobs',          icon: '⟳', roles: ['admin','operator','client','reseller'], resellerPerm: 'can_manage_jobs', perms: ['can_view_clients'] },
    { path: '/logs',      label: 'Logs',          icon: '≡', roles: ['admin','operator','client','reseller'], resellerPerm: 'can_view_reports', perms: ['can_view_clients'] },
    { path: '/users',     label: 'Usuarios',      icon: '◈', roles: ['admin'] },
    { path: '/users',     label: 'Usuarios',      icon: '◈', roles: ['reseller'],                resellerPerm: ['can_manage_users', 'can_manage_account'] },
    { path: '/resellers', label: 'Resellers',     icon: '◇', roles: ['admin'] },
    { path: '/restores',  label: 'Restaurar',     icon: '↺', roles: ['admin','operator','client','reseller'], resellerPerm: 'can_view_clients', perms: ['can_view_clients'] },
    { path: '/updates',   label: 'Actualizaciones', icon: '↑', roles: ['admin','operator'], perms: ['can_manage_servers'] },
    { path: '/settings',  label: 'Configuración', icon: '⚙', roles: ['admin'] },
    { path: '/audit',     label: 'Auditoría',     icon: '⊙', roles: ['admin'] },
    { path: '/profile',   label: 'Mi Perfil',     icon: '◎', roles: ['admin','operator','client','reseller'] },
  ];

  const nav = allNav.filter(item => {
    if (!me) return false;
    if (!item.roles.includes(me.role)) return false;
    if (item.show === false) return false;
    if (me.role === 'reseller' && item.resellerPerm) {
      return [].concat(item.resellerPerm).some(p => !!resellerPerms[p]);
    }
    if (me.role === 'reseller') return true;
    if (item.perms && me.role === 'operator') {
      return item.perms.some(p => perms[p]);
    }
    return true;
  });

  const roleLabel = { admin: 'ADMIN', operator: 'OPERADOR', client: 'CLIENTE', reseller: 'RESELLER' };
  const roleColor = { admin: 'var(--red)', operator: 'var(--accent)', client: 'var(--green)', reseller: 'var(--yellow)' };

  const sidebarInner = (showClose) => (
    <>
      <div style={{ padding: '20px 16px 16px', borderBottom: '1px solid var(--border)', position: 'relative' }}>
        {showClose && (
          <button onClick={() => setSidebarOpen(false)} style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>✕</button>
        )}
        {branding.logo_url ? (
          <img src={branding.logo_url} alt={branding.platform_name || 'OpenBackup'}
            style={{ width: '100%', maxWidth: 180, height: 'auto', maxHeight: 56, objectFit: 'contain' }}
            onError={e => { e.target.style.display = 'none'; }} />
        ) : (
          <Logo size={17} />
        )}
      </div>
      <nav style={{ padding: '16px 0' }}>
        {nav.map((item, i) => {
          const active = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
          return (
            <div key={i} onClick={() => { navigate(item.path); if (isMobile) setSidebarOpen(false); }} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 20px', cursor: 'pointer', fontSize: 13,
              color: active ? 'var(--accent)' : 'var(--muted)',
              background: active ? 'rgba(0,212,255,0.06)' : 'transparent',
              borderLeft: `2px solid ${active ? 'var(--accent)' : 'transparent'}`,
              transition: 'all 0.15s',
            }}
              onMouseEnter={e => { if (!active) e.currentTarget.style.color = 'var(--text)'; }}
              onMouseLeave={e => { if (!active) e.currentTarget.style.color = 'var(--muted)'; }}>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 14 }}>{item.icon}</span>
              {item.label}
            </div>
          );
        })}
      </nav>
      <div style={{ borderTop: '1px solid var(--border)', marginTop: 8, paddingTop: 8, padding: '8px 20px 16px' }}>
        <button onClick={onLogout} style={{ width: '100%', background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted)', padding: '6px 12px', fontSize: 11, fontFamily: 'var(--mono)', cursor: 'pointer', letterSpacing: 1 }}>
          CERRAR SESIÓN
        </button>
      </div>
      <div style={{ padding: '12px 20px 16px', borderTop: '1px solid var(--border)' }}>
        <div onClick={() => { navigate('/profile'); if (isMobile) setSidebarOpen(false); }}
          style={{ cursor: 'pointer' }}
          onMouseEnter={e => e.currentTarget.style.opacity = '0.7'}
          onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{me?.email}</div>
          <div style={{ fontSize: 10, fontFamily: 'var(--mono)', color: roleColor[me?.role] || 'var(--muted)', letterSpacing: 1 }}>{roleLabel[me?.role] || '—'}</div>
        </div>
      </div>
    </>
  );

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg)', alignItems: 'flex-start' }}>
      {isMobile ? (
        <>
          {/* MOBILE HEADER BAR */}
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: 48, background: 'var(--surface)', borderBottom: '1px solid var(--border)', zIndex: 100, display: 'flex', alignItems: 'center', padding: '0 16px', gap: 12 }}>
            <button onClick={() => setSidebarOpen(true)} style={{ background: 'none', border: 'none', color: 'var(--text)', cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: 0 }}>☰</button>
            {branding.logo_url ? (
              <img src={branding.logo_url} alt={branding.platform_name || 'OpenBackup'}
                style={{ height: 30, width: 'auto', maxWidth: 150, objectFit: 'contain' }}
                onError={e => { e.target.style.display = 'none'; }} />
            ) : (
              <Logo size={13} />
            )}
            <button
              onClick={toggleTheme}
              title={theme === 'dark' ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro'}
              style={{
                marginLeft: 'auto',
                width: 40, height: 40,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 8,
                cursor: 'pointer',
                transition: 'all 0.2s',
                background: theme === 'dark' ? '#fbbf24' : 'transparent',
                border: theme === 'dark' ? '1px solid #fbbf24' : '1px solid var(--border)',
                color: theme === 'dark' ? '#78350f' : 'var(--text)',
                flexShrink: 0,
              }}
            >
              {theme === 'dark' ? <Sun size={20} strokeWidth={2.5} /> : <Moon size={20} strokeWidth={2.0} />}
            </button>
          </div>
          {/* OVERLAY + DRAWER */}
          {sidebarOpen && (
            <>
              <div onClick={() => setSidebarOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 150 }} />
              <div style={{ position: 'fixed', top: 0, left: 0, height: '100vh', width: 220, zIndex: 200, background: 'var(--surface)', borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
                {sidebarInner(true)}
              </div>
            </>
          )}
        </>
      ) : (
        /* DESKTOP SIDEBAR */
        <div style={{ width: 220, minWidth: 220, background: 'var(--surface)', borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', position: 'sticky', top: 0, height: '100vh', overflowY: 'auto', flexShrink: 0 }}>
          {sidebarInner(false)}
        </div>
      )}

      {/* MAIN */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', paddingTop: isMobile ? 48 : 0 }}>
        {!isMobile && (
          <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 12, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>
              {breadcrumbLabel(location.pathname)}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--muted)' }}>
                {window.location.host}
              </div>
              <button
                onClick={toggleTheme}
                title={theme === 'dark' ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro'}
                style={{
                  width: 40, height: 40,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 8,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  background: theme === 'dark' ? '#fbbf24' : 'transparent',
                  border: theme === 'dark' ? '1px solid #fbbf24' : '1px solid var(--border)',
                  color: theme === 'dark' ? '#78350f' : 'var(--text)',
                }}
              >
                {theme === 'dark' ? <Sun size={20} strokeWidth={2.5} /> : <Moon size={20} strokeWidth={2.0} />}
              </button>
            </div>
          </div>
        )}
        {pendingMsg && (
          <div role="alert" style={{ padding: '10px 16px', background: 'rgba(255,77,109,0.12)', borderBottom: '1px solid var(--red)', color: 'var(--red)', fontSize: 12, fontFamily: 'var(--mono)', overflowWrap: 'anywhere' }}>
            ⚠ {pendingMsg}
          </div>
        )}
        <div style={{ flex: 1, padding: isMobile ? 12 : 24, overflowY: 'auto' }}>
          {children}
        </div>
      </div>
    </div>
  );
}
